import { el, btn } from '@/ui/dom';
import { confirmAction } from '@/ui/confirm';
import { openSheet, sheetInput, type SheetHandle } from '@/ui/social/sheet';
import { CHEVRON_SVG, ridButton } from '@/ui/social/clubUi';
import { sfxCollect, sfxUiTick } from '@/audio/sfx';
import { hapticImpact, hapticTick } from '@/ui/haptics';
import {
  BLOCKED_LABEL,
  CLUB_REPORT_REASONS,
  PLAYER_REPORT_REASONS,
  REPORT_NOTE_MAX,
  blockResultMessage,
  blockedList,
  isBlockedName,
  noteBlocked,
  noteUnblocked,
  reportResultMessage,
  setBlockedList,
  type ReportContextId,
  type ReportReasonId,
} from '@/social/moderation';
import type { SocialService } from '@/social/SocialService';

/**
 * playerSheet.ts — M156, REPORT + BLOCK (App Store Guideline 1.2). Every
 * place the game shows a name another player chose opens one of these:
 *
 *  - openPlayerSheet  one racer (or one club name): REPORT, and BLOCK /
 *                     UNBLOCK for a racer. Opened by tapping a name (race
 *                     lobby seats, Racer Club members) or from the picker.
 *  - openReportPicker "REPORT A RACER" on the online lobby / results / tour
 *                     standings: choose who, then the racer sheet.
 *  - openBlockedList  SETTINGS → RACER ID → BLOCKED PLAYERS: unblock.
 *
 * All three are the Racer Club sheet (ui/social/sheet.ts) in the dashboard's
 * button language, so they look and behave like every other club modal. The
 * block confirmation goes through ui/confirm.ts (the M115 destructive-action
 * rule). Nothing here ever tells the player whether a name belongs to a real
 * account — computer racers wear pool handles and are never revealed.
 */

export const REPORT_FLAG_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M5 2.4c.55 0 1 .45 1 1V4h11.4c.83 0 1.3.95.79 1.6L16.1 8.6l2.09 3c.47.66 0 1.57-.81 1.57H6v8.43a1 1 0 1 1-2 0V3.4c0-.55.45-1 1-1Z"/></svg>';
const BLOCK_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.6" fill="none" stroke="currentColor" stroke-width="2.4"/><path d="M6.2 6.2 17.8 17.8" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';

export interface ModerationDeps {
  social: SocialService;
  /** Signed in with a Racer ID right now? Reports and blocks need one. */
  signedIn: () => boolean;
  reducedMotion: () => boolean;
}

export interface PlayerSheetOptions {
  deps: ModerationDeps;
  /** The REAL name as the room / server relayed it (never a masked label). */
  name: string;
  kind?: 'player' | 'club';
  /** Known for Racer Club rows (block by id); unknown in races (by name). */
  userId?: string | null;
  context: ReportContextId;
  /** The online room code, when the sheet opens from a race. */
  room?: string | null;
  /** A result line to lead with (after a block / unblock). */
  notice?: { text: string; ok: boolean };
}

const upper = (s: string): string => s.toUpperCase();

/** One racer (or one club name): REPORT, and BLOCK / UNBLOCK for a racer. */
export function openPlayerSheet(o: PlayerSheetOptions): SheetHandle {
  const kind = o.kind ?? 'player';
  const blocked = kind === 'player' && isBlockedName(o.name);
  const shown = blocked ? upper(BLOCKED_LABEL) : upper(o.name);
  const sheet = openSheet({
    kicker: kind === 'club' ? 'CLUB NAME' : 'RACER',
    title: shown,
    className: 'crew-sheet-mod',
    reducedMotion: o.deps.reducedMotion(),
  });
  const b = sheet.body;
  if (o.notice) {
    el('div', `crew-status mod-notice ${o.notice.ok ? 'ok' : 'bad'}`, b).textContent = o.notice.text;
  }

  if (!o.deps.signedIn()) {
    el('div', 'crew-fine', b).textContent =
      'Reporting and blocking use your Racer ID. Create one or sign in from the title screen, then try again.';
    const actions = el('div', 'crew-sheet-actions', b);
    ridButton(actions, { variant: 'glass', label: 'OK', noChevron: true }, () => sheet.close());
    return sheet;
  }

  el('div', 'crew-fine', b).textContent =
    kind === 'club'
      ? 'An offensive club name? Report it — we review every report within 24 hours.'
      : 'An offensive name, cheating or bullying? Report it — we review every report within 24 hours.';

  const actions = el('div', 'crew-sheet-actions crew-sheet-stack', b);
  ridButton(
    actions,
    {
      variant: 'glass',
      icon: REPORT_FLAG_SVG,
      label: kind === 'club' ? 'REPORT THIS CLUB NAME' : blocked ? 'REPORT THIS RACER' : `REPORT ${shown}`,
      className: 'mod-report-btn',
    },
    () => showReportForm(sheet, o, kind, shown),
  );
  if (kind === 'player') {
    if (blocked) {
      btn(`UNBLOCK`, 'btn btn-ghost mod-unblock-btn', actions, () => {
        void unblock(o);
      });
    } else {
      const block = btn('', 'btn btn-danger-ghost mod-block-btn', actions, () => confirmBlock(o));
      el('span', 'mod-btn-ic', block).innerHTML = BLOCK_SVG;
      el('span', '', block).textContent = `BLOCK ${shown}`;
    }
  }
  return sheet;
}

/** The REPORT step, in the same sheet: why, an optional note, send. */
function showReportForm(
  sheet: SheetHandle,
  o: PlayerSheetOptions,
  kind: 'player' | 'club',
  shown: string,
): void {
  sheet.setTitle(kind === 'club' ? 'REPORT CLUB NAME' : `REPORT ${shown}`);
  const b = sheet.body;
  b.replaceChildren();
  el('div', 'mod-label', b).textContent = "WHAT'S WRONG?";
  const list = el('div', 'mod-reasons', b);
  list.setAttribute('role', 'radiogroup');
  let reason: ReportReasonId | null = null;
  const reasons = kind === 'club' ? CLUB_REPORT_REASONS : PLAYER_REPORT_REASONS;
  const rows: HTMLButtonElement[] = [];
  const send = { el: null as HTMLButtonElement | null };
  for (const r of reasons) {
    const row = el('button', 'mod-reason', list);
    row.type = 'button';
    row.setAttribute('role', 'radio');
    row.setAttribute('aria-checked', 'false');
    el('span', 'mod-reason-dot', row);
    el('span', 'mod-reason-label', row).textContent = r.label;
    row.addEventListener('click', () => {
      sfxUiTick();
      hapticTick();
      reason = r.id;
      for (const x of rows) x.setAttribute('aria-checked', String(x === row));
      if (send.el) send.el.disabled = false;
    });
    rows.push(row);
  }
  const note = sheetInput(b, {
    className: 'crew-input mod-note',
    placeholder: 'ADD A NOTE (OPTIONAL)',
    maxLength: REPORT_NOTE_MAX,
    label: 'A note for the moderators (optional)',
  });
  const status = el('div', 'crew-status', b);
  const actions = el('div', 'crew-sheet-actions', b);
  ridButton(actions, { variant: 'glass', label: 'BACK', noChevron: true }, () => {
    openPlayerSheet(o);
  });
  const sendBtn = ridButton(actions, { variant: 'gold', icon: REPORT_FLAG_SVG, label: 'SEND REPORT', noChevron: true }, () => {
    if (!reason) return;
    sendBtn.el.disabled = true;
    status.className = 'crew-status';
    status.textContent = 'SENDING…';
    void o.deps.social
      .reportPlayer({
        kind,
        name: o.name,
        reason,
        context: o.context,
        room: o.room ?? null,
        details: note.value.trim() || null,
      })
      .then((res) => {
        if (res.status === 'ok') {
          showReportSent(sheet, o, kind, shown);
          return;
        }
        sendBtn.el.disabled = false;
        status.className = 'crew-status bad';
        status.textContent = reportResultMessage(res.status);
      });
  });
  send.el = sendBtn.el;
  sendBtn.el.disabled = true;
}

/** The thank-you face after a report lands (+ the offer to block, too). */
function showReportSent(
  sheet: SheetHandle,
  o: PlayerSheetOptions,
  kind: 'player' | 'club',
  shown: string,
): void {
  sfxCollect();
  hapticImpact();
  sheet.setTitle('REPORT SENT');
  const b = sheet.body;
  b.replaceChildren();
  const check = el('div', 'mod-sent', b);
  check.innerHTML =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5 10 17.5 19 7.5" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  el('div', 'crew-fine', b).textContent =
    'Thank you. We review every report within 24 hours. When a name breaks our Community Rules we change it, and players who keep breaking them are suspended.';
  const actions = el('div', 'crew-sheet-actions crew-sheet-stack', b);
  if (kind === 'player' && !isBlockedName(o.name)) {
    const block = btn('', 'btn btn-danger-ghost mod-block-btn', actions, () => confirmBlock(o));
    el('span', 'mod-btn-ic', block).innerHTML = BLOCK_SVG;
    el('span', '', block).textContent = `ALSO BLOCK ${shown}`;
  }
  ridButton(actions, { variant: 'glass', label: 'DONE', noChevron: true }, () => sheet.close());
}

/** BLOCK goes through the shared confirm dialog (it closes the sheet); the
 *  racer sheet reopens with the result. */
function confirmBlock(o: PlayerSheetOptions): void {
  const who = upper(o.name);
  confirmAction({
    title: `BLOCK ${who}?`,
    body:
      "You won't see their name in online races, and they can't send you friend requests or club invites. " +
      'You can unblock them any time in SETTINGS → RACER ID.',
    confirmLabel: 'BLOCK',
    reducedMotion: o.deps.reducedMotion(),
    onConfirm: () => {
      void o.deps.social.blockPlayer({ name: o.name, userId: o.userId ?? undefined }).then((res) => {
        if (res.status === 'ok') noteBlocked(o.name, o.userId ?? null);
        openPlayerSheet({
          ...o,
          notice: { text: blockResultMessage(res.status, o.name, true), ok: res.status === 'ok' },
        });
      });
    },
  });
}

async function unblock(o: PlayerSheetOptions): Promise<void> {
  const known = blockedList().find((b) => b.name.trim().toLowerCase() === o.name.trim().toLowerCase());
  const userId = o.userId ?? known?.userId ?? null;
  // A name-only block (a race name that matched no account) lives in this
  // session alone — there is nothing on the server to undo.
  const res = userId ? await o.deps.social.unblockPlayer(userId) : { status: 'ok' };
  if (res.status === 'ok') noteUnblocked(o.name);
  openPlayerSheet({
    ...o,
    notice: { text: blockResultMessage(res.status, o.name, false), ok: res.status === 'ok' },
  });
}

// ---- REPORT A RACER (online lobby / results / standings) ----------------------------------------

export interface ReportPickerOptions {
  deps: ModerationDeps;
  /** Everyone else in this race, by their REAL names. */
  names: readonly string[];
  context: ReportContextId;
  room?: string | null;
}

export function openReportPicker(o: ReportPickerOptions): SheetHandle {
  const sheet = openSheet({
    kicker: 'REPORT OR BLOCK',
    title: 'CHOOSE A RACER',
    className: 'crew-sheet-mod',
    reducedMotion: o.deps.reducedMotion(),
  });
  const b = sheet.body;
  const seen = new Set<string>();
  const names = o.names.filter((n) => {
    const k = n.trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (names.length === 0) {
    el('div', 'crew-fine', b).textContent = "There's nobody else in this race yet.";
  } else {
    el('div', 'crew-fine', b).textContent = 'Pick the racer you want to report or block.';
    const list = el('div', 'mod-picks', b);
    for (const name of names) {
      const row = el('button', 'mod-pick', list);
      row.type = 'button';
      el('span', 'mod-pick-name', row).textContent = upper(isBlockedName(name) ? BLOCKED_LABEL : name);
      el('span', 'mod-pick-chev', row).innerHTML = CHEVRON_SVG;
      row.addEventListener('click', () => {
        sfxUiTick();
        hapticTick();
        openPlayerSheet({ deps: o.deps, name, context: o.context, room: o.room ?? null });
      });
    }
  }
  const actions = el('div', 'crew-sheet-actions', b);
  ridButton(actions, { variant: 'glass', label: 'DONE', noChevron: true }, () => sheet.close());
  return sheet;
}

// ---- SETTINGS → RACER ID → BLOCKED PLAYERS --------------------------------------------------------

export function openBlockedList(deps: ModerationDeps): SheetHandle {
  const sheet = openSheet({
    kicker: 'RACER ID',
    title: 'BLOCKED PLAYERS',
    className: 'crew-sheet-mod',
    reducedMotion: deps.reducedMotion(),
  });
  const b = sheet.body;
  const list = el('div', 'mod-picks', b);
  const status = el('div', 'crew-status', b);
  const actions = el('div', 'crew-sheet-actions', b);
  ridButton(actions, { variant: 'glass', label: 'DONE', noChevron: true }, () => sheet.close());

  const paint = (): void => {
    list.replaceChildren();
    const rows = blockedList();
    if (rows.length === 0) {
      el('div', 'crew-fine', list).textContent =
        "You haven't blocked anyone. To block a player, tap their name in a race lobby, on the results or in the Racer Club.";
      return;
    }
    for (const p of rows) {
      const row = el('div', 'mod-pick mod-blocked', list);
      el('span', 'mod-pick-name', row).textContent = upper(p.name);
      btn('UNBLOCK', 'btn btn-ghost mod-unblock-btn', row, () => {
        void (async () => {
          const res = p.userId ? await deps.social.unblockPlayer(p.userId) : { status: 'ok' };
          if (res.status === 'ok') noteUnblocked(p.name);
          status.className = `crew-status ${res.status === 'ok' ? 'ok' : 'bad'}`;
          status.textContent = blockResultMessage(res.status, p.name, false);
          paint();
        })();
      });
    }
  };
  paint();
  if (deps.signedIn()) {
    status.textContent = 'LOADING…';
    void deps.social.myBlocks().then((rows) => {
      if (rows) setBlockedList(rows);
      status.textContent = rows ? '' : "Couldn't load the list — check your connection.";
      paint();
    });
  }
  return sheet;
}
